import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { FormControl, InputLabel, Menu, MenuItem, Select } from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const PhoneNumberMessageOptionsMenu = ({ anchorEl, handleClose, menuOpen, phoneNumber, setTabOpen = null }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const { user } = useContext(AuthContext);
  const { whatsApps } = useWhatsApps();
  
  const history = useHistory();
  const defaultWhatsapp = whatsApps.length === 1 ? whatsApps[0] : null;

  const [chatWithNumberConfirmationOpen, setChatWithNumberConfirmationOpen] = useState(false);
  const [userQueues, setUserQueues] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");

  const { getUserQueuesIds, setShouldSyncRouteOnMenu } = useGlobalRegisters();



  //  ***************
  //  ** Callbacks **
  //  ***************
  const setDefaultWhatsapp = useCallback(() => {
    setWhatsappId(!whatsappId && defaultWhatsapp ? defaultWhatsapp.id : whatsappId);
  }, [whatsappId, defaultWhatsapp]);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (chatWithNumberConfirmationOpen && defaultWhatsapp) setDefaultWhatsapp();
  }, [chatWithNumberConfirmationOpen, defaultWhatsapp, setDefaultWhatsapp]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchUserQueuesIds = async () => {
        try {
          const userQueuesIds = await getUserQueuesIds(user.id);
          setUserQueues(userQueuesIds);
        }
        catch (exception) {
					console.log("Phone Number Message Options Menu Use Effect Exception:", exception);
          toastError(exception);
        }
      };

      fetchUserQueuesIds();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [getUserQueuesIds, user.id]);



  //  ***************
  //  ** Functions **
  //  ***************
  const onClose = () => {
    setChatWithNumberConfirmationOpen(false);
    setWhatsappId("");
  };

  const handleChatWithNumberConfirmationModal = () => {
    setChatWithNumberConfirmationOpen(true);
    handleClose();
  };

  const handleCopyPhoneNumber = async () => {
    handleClose();
    await navigator.clipboard.writeText(phoneNumber);
  };

  const handleOpenChatWithPhoneNumber = async () => {
    try {
      if (!whatsappId) {
        toast.info(i18n.t("newTicketModal.toasts.whatsappNotSelected"));
        return;
      }

      const { data } = await api.post("/phoneNumberMessageTicket", { phoneNumber, whatsappId });
      
      if (!data.success) {
        toast.info(i18n.t(data.message));
        return;
      }

      const { ticket } = data;
      const pendingTicketCondition = ticket.status === "pending" && (ticket.queueId === null || userQueues.includes(ticket.queueId));
      const openTicketAndAdminUserCondition = ticket.status === "open" && user.profile === "admin";
      const openTicketAndCommonUserCondition = ticket.status === "open" && ticket.userId === user.id;

      // ***---- User can see Ticket ----***
      if (pendingTicketCondition || openTicketAndAdminUserCondition || openTicketAndCommonUserCondition) {
        if (setTabOpen) setTabOpen(ticket.status);
        localStorage.setItem("tabOpenTickets", ticket.status);
        DefaultFunctionsUtils.redirectToTicketsPage(history, `/${ticket.id}`);
        setShouldSyncRouteOnMenu(true);
      }

      // ***---- User cannot see Ticket ----***
      else {
        const message = ticket.status === "pending"
          ? `
            ${i18n.t("backendErrors.ERR_OTHER_OPEN_TICKET")}
            ${i18n.t("backendErrors.ERR_OTHER_OPEN_TICKET_QUEUE")}
            ${ticket.queue?.name}
          `
          : `
            ${i18n.t("backendErrors.ERR_OTHER_OPEN_TICKET")}
            ${i18n.t("backendErrors.ERR_OTHER_OPEN_TICKET")}
            ${ticket.user?.name}
          `;

        toast.info(message);
      }
    }
    catch (exception) {
      console.log("- Phone Number Message Options Menu Exception:", exception);
      toastError(exception);
    }
    finally {
      onClose();
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <>
    <ConfirmationModal
      title={i18n.t("phoneNumberOptionsMenu.confirmationModal.title")}
      open={chatWithNumberConfirmationOpen}
      onClose={onClose}
      onConfirm={async () => await handleOpenChatWithPhoneNumber()}
    >
      <FormControl variant="outlined" margin="dense" fullWidth>
        <InputLabel>{i18n.t("phoneNumberOptionsMenu.confirmationModal.fields.connection")}</InputLabel>

        <Select
          fullWidth
          label={i18n.t("phoneNumberOptionsMenu.confirmationModal.fields.connection")}
          onChange={(event) => setWhatsappId(event.target.value)}
          value={whatsappId}
        >
          <MenuItem value={""}>&nbsp;</MenuItem>
          {whatsApps.map(whatsapp => <MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>)}
        </Select>
      </FormControl>
    </ConfirmationModal>

    <Menu
      anchorEl={anchorEl}
      getContentAnchorEl={null}
      anchorOrigin={{vertical: "bottom", horizontal: "right"}}
      transformOrigin={{vertical: "top", horizontal: "right"}}

      open={menuOpen}
      onClose={handleClose}
      onContextMenu={event => event.preventDefault()}
    >
      {/* ***---- Action: Chat with Phone Number ----*** */}
      <MenuItem onClick={handleChatWithNumberConfirmationModal}>{i18n.t("phoneNumberOptionsMenu.chat")}</MenuItem>

      {/* ***---- Action: Copy Phone Number */}
      <MenuItem onClick={async () => await handleCopyPhoneNumber()}>{i18n.t("phoneNumberOptionsMenu.copy")}</MenuItem>

      {/* ***---- Action: Close ----*** */}
      <MenuItem onClick={handleClose}>{i18n.t("phoneNumberOptionsMenu.close")}</MenuItem>
    </Menu>
    </>
  );
};

export default PhoneNumberMessageOptionsMenu;